import ACTIVITY_ICONS from 'components/activity/activityIcons';
import activitySentence from 'components/activity/activitySentence';
import { dayLabel, durationLabel, fromIsoDay } from 'helper/timeLabels';

const nameOf = (person) => (person ? `${person.firstname} ${person.lastname}`.trim() : '');
const excerpt = (value) => `« ${String(value).slice(0, 80)}${String(value).length > 80 ? '…' : ''} »`;
const isEmpty = (value) => value === null || value === '' || value === undefined;
// What a step did, after « Vous avez » or « William a ».
const KIND_PHRASES = {
  created: 'créé cette tâche',
  done: 'marqué cette tâche comme terminée',
  reopened: 'rouvert cette tâche',
  updated: 'modifié cette tâche',
};

/**
 * The timeline of a task's panel (components/activity/ActivityTimeline), from
 * its history (crmApi.getTaskActivity), the newest first, one sentence a step
 * as the user reads it (activitySentence): « Vous avez créé cette tâche »,
 * « William a défini l’échéance : jeu. 22 oct. ». A change of several fields
 * gives a sentence a field. `labels`: `text` (taskActivity.*, the names of the
 * fields), `typeLabel`, the `team` and the `companies` to name the ids, the
 * task's `creator` (createdByPerson), `meId`.
 */
const taskActivityItems = (events, { text, typeLabel, team, companies, creator, meId }) => {
  const person = (id) => nameOf(team.find((member) => member.id === id));
  const linksShown = (list) => list.map((link) => link.label).join(', ');
  // The sentence of one field changed by `actor`.
  const fieldSentence = (actor, field, change) => {
    const say = (phrase) => activitySentence(actor, meId, phrase);
    if (field === 'links') return change.to?.length ? say(`modifié les fiches liées : ${linksShown(change.to)}`) : say('retiré les fiches liées');
    const after = change[1];
    if (field === 'assigneeId') {
      // A task assigned to no one stays with whoever created it.
      const targetId = after || creator?.id;
      if (targetId && targetId === meId) {
        return actor?.id === meId ? 'Vous vous êtes attribué cette tâche' : `${actor?.firstname || 'Quelqu’un'} vous a attribué cette tâche`;
      }
      return say(`attribué cette tâche à ${(after ? person(after) : nameOf(creator)) || 'quelqu’un'}`);
    }
    if (field === 'title') return say(`renommé cette tâche : ${excerpt(after)}`);
    if (field === 'type') return say(`changé le type : ${typeLabel(after)}`);
    if (field === 'note') return isEmpty(after) ? say('retiré la description') : say(`modifié la description : ${excerpt(after)}`);
    if (field === 'dueOn') return isEmpty(after) ? say('retiré l’échéance') : say(`défini l’échéance : ${dayLabel(fromIsoDay(after)).toLowerCase()}`);
    if (field === 'dueTime') return isEmpty(after) ? say('retiré l’heure') : say(`défini l’heure : ${String(after).slice(0, 5)}`);
    if (field === 'durationMin') return say(`défini la durée : ${durationLabel(Number(after))}`);
    if (field === 'companyId') {
      return isEmpty(after) ? say('retiré l’entreprise') : say(`lié cette tâche à ${companies.find((item) => item.companyId === after)?.company ?? text('otherCompany')}`);
    }
    if (field === 'contactName') return isEmpty(after) ? say('retiré le contact') : say(`défini le contact : ${after}`);
    return say(`modifié le champ « ${text(`field_${field}`)} »`);
  };
  return events
    .flatMap((event) => {
      const base = { icon: ACTIVITY_ICONS[event.kind] ?? ACTIVITY_ICONS.updated, at: new Date(event.at) };
      const fields = Object.entries(event.changes ?? {});
      if (event.kind !== 'updated' || !fields.length) {
        return [{ ...base, id: event.id, text: activitySentence(event.actor, meId, KIND_PHRASES[event.kind] ?? KIND_PHRASES.updated) }];
      }
      return fields.map(([field, change]) => ({ ...base, id: `${event.id}-${field}`, text: fieldSentence(event.actor, field, change) }));
    })
    .sort((a, b) => b.at - a.at);
};

export default taskActivityItems;
